import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeleteDevice,
  useRemoveProfilePhoto,
  useUpdateUser,
  useUploadProfilePhoto,
} from '../../hooks/queries';
import {
  ProfilePersonalSection,
  ProfilePhotoSection,
  type ProfilePersonalData,
} from '@tiaki/profile-ui';
import { Grid, HStack, Text, VStack } from '@chakra-ui/react';
import { UserDetailData } from '../../types/userDetails';
import { userDetailSchema } from '../../validations/user.validation';
import { toaster } from '../ui/toaster';
import { UserFormInitialValues } from '../../types/UserFormInitialValue';
import UserDevicesSection from './UserDevicesSection';
import { User } from '../../services';
import { Button, DialogComponent } from '@themes/aegix';

/**
 * Map the shared `{ first/last/email/phone }` shape onto SMS's
 * `{ first_name, last_name, email, cell_phone }` zod schema, then translate
 * any issues back to shared field keys for inline errors. Returns null when
 * valid (the shared section treats a null/empty map as "ok to save").
 */
function validatePersonalDetails(
  value: ProfilePersonalData,
): Partial<Record<keyof ProfilePersonalData, string>> | null {
  const result = userDetailSchema.safeParse({
    first_name: value.firstName,
    last_name: value.lastName,
    email: value.email,
    cell_phone: value.phone,
  });
  if (result.success) return null;

  const fieldMap: Record<string, keyof ProfilePersonalData> = {
    first_name: 'firstName',
    last_name: 'lastName',
    email: 'email',
    cell_phone: 'phone',
  };
  const errors: Partial<Record<keyof ProfilePersonalData, string>> = {};
  for (const issue of result.error.issues) {
    const key = fieldMap[issue.path[0] as string];
    if (key && !errors[key]) errors[key] = issue.message;
  }
  return errors;
}

/**
 * ProfileDetails — Personal-tab content for the Profile drawer.
 *
 * Renders account-identity surfaces only: avatar editor (shared
 * `ProfilePhotoSection`, parity with AIM), name/email/phone editor,
 * and the user's registered devices. Roles and Sites live on the Assignments
 * tab (they describe what the user is assigned to, not who they are).
 *
 * Password reset lives in the drawer FOOTER (above Sign Out), in the
 * self-contained `ResetPasswordButton` rendered via the shell's `footerSlot`
 * from `ProfileDrawer.tsx`, not on the Personal tab.
 */
interface ProfileDetailsProps {
  currentUser?: UserFormInitialValues;
  isLoadingCurrentUser: boolean;
}

const ProfileDetails = ({
  currentUser,
  isLoadingCurrentUser,
}: ProfileDetailsProps) => {
  const { t } = useTranslation();
  const updateUserMutation = useUpdateUser();
  const deleteDeviceMutation = useDeleteDevice();
  const uploadPhotoMutation = useUploadProfilePhoto();
  const removePhotoMutation = useRemoveProfilePhoto();
  const [deviceToRemove, setDeviceToRemove] = useState<{
    tokenId: string;
    platform: string;
  } | null>(null);

  // The shared photo section is presentational — the adapter owns the
  // mutations and toasts (same split as AIM's `useProfileData`).
  const handlePhotoChange = (file: File) => {
    if (currentUser?.id == null) return;
    uploadPhotoMutation.mutate(
      { id: Number(currentUser.id), file },
      {
        onSuccess: () =>
          toaster.create({
            title: t('profile.photoUpdated', 'Profile photo updated.'),
            type: 'success',
            closable: true,
          }),
        onError: () =>
          toaster.create({
            title: t('profile.photoUploadFailed', 'Failed to upload photo.'),
            type: 'error',
            closable: true,
          }),
      },
    );
  };

  const handlePhotoRemove = () => {
    if (currentUser?.id == null) return;
    removePhotoMutation.mutate(Number(currentUser.id), {
      onSuccess: () =>
        toaster.create({
          title: t('profile.photoRemoved', 'Profile photo removed.'),
          type: 'success',
          closable: true,
        }),
      onError: () =>
        toaster.create({
          title: t('profile.photoRemoveFailed', 'Failed to remove photo.'),
          type: 'error',
          closable: true,
        }),
    });
  };

  const handleSaveUserDetail = async (
    data: ProfilePersonalData,
  ): Promise<void> => {
    const payload: Partial<User> = {};
    if (data.firstName !== currentUser?.first_name)
      payload.first_name = data.firstName;
    if (data.lastName !== currentUser?.last_name)
      payload.last_name = data.lastName;
    if (data.email !== currentUser?.email) payload.email = data.email;
    if (data.phone !== (currentUser?.cell_phone ?? ''))
      payload.cell_phone = data.phone;

    if (Object.keys(payload).length === 0) {
      toaster.create({
        title: t('profile.noChanges', 'No changes detected to update.'),
        type: 'info',
        closable: true,
      });
      return;
    }

    // The shared section keeps edit mode open if onSave rejects, so the
    // adapter owns the error toast (the lib has no toaster).
    try {
      await updateUserMutation.mutateAsync({
        id: Number(currentUser?.id),
        data: payload as UserDetailData,
      });
    } catch (error: unknown) {
      toaster.create({
        title: t('profile.saveFailed', 'Failed to save changes'),
        description:
          error instanceof Error
            ? error.message
            : t('common.unexpectedError', 'An unexpected error occurred. Please try again.'),
        type: 'error',
        closable: true,
      });
      throw error;
    }
  };

  const handleRemoveDevice = (tokenId: string, platform: string) => {
    setDeviceToRemove({ tokenId, platform });
  };

  const confirmRemoveDevice = () => {
    if (deviceToRemove) {
      deleteDeviceMutation.mutate(deviceToRemove.tokenId);
      setDeviceToRemove(null);
    }
  };

  return (
    <Grid gap={4}>
      <ProfilePhotoSection
        profilePhoto={currentUser?.profile_photo}
        userName={`${currentUser?.first_name || ''} ${
          currentUser?.last_name || ''
        }`.trim()}
        onPhotoChange={handlePhotoChange}
        onPhotoRemove={handlePhotoRemove}
        isUploading={uploadPhotoMutation.isPending}
      />

      <ProfilePersonalSection
        value={{
          firstName: currentUser?.first_name || '',
          lastName: currentUser?.last_name || '',
          email: currentUser?.email || '',
          phone: currentUser?.cell_phone || '',
        }}
        onSave={handleSaveUserDetail}
        isSaving={isLoadingCurrentUser}
        validate={validatePersonalDetails}
      />

      <UserDevicesSection
        devices={currentUser?.expiring_fcm_tokens || []}
        onRemoveDevice={handleRemoveDevice}
        canDelete={true}
      />

      <DialogComponent
        isOpen={deviceToRemove !== null}
        onClose={() => setDeviceToRemove(null)}
        size="sm"
        headingText={t('profile.removeDevice', 'Remove Device')}
        e2eTitle="remove-device-dialog"
      >
        <VStack gap={6} align="stretch" pt={2}>
          <Text color="text.secondary">
            {t(
              'profile.removeDeviceConfirm',
              'Remove this {{platform}} device? You will need to log in again to receive notifications on this device.',
              { platform: deviceToRemove?.platform ?? '' },
            )}
          </Text>
          <HStack gap={4} justifyContent="flex-end">
            <Button visual="secondary" onClick={() => setDeviceToRemove(null)} data-e2e="cancel-button">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button visual="primary" onClick={confirmRemoveDevice} data-e2e="remove-button">
              {t('common.remove', 'Remove')}
            </Button>
          </HStack>
        </VStack>
      </DialogComponent>
    </Grid>
  );
};

export default ProfileDetails;
